import { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { useQuote } from "../lib/quoteContext";
import { SERVICES } from "../lib/site";

const HOME_SIZES = [
  "Studio",
  "1 Bedroom",
  "2 Bedrooms",
  "3 Bedrooms",
  "4 Bedrooms",
  "5+ Bedrooms",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  phone: "",
  email: "",
  zip_code: "",
  home_size: "",
  service_type: "",
};

export default function QuoteModal() {
  const { open, closeQuote, preselectedService } = useQuote();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...initialForm, service_type: preselectedService || "" });
      setErrors({});
      setSuccess(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, preselectedService]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) closeQuote();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeQuote]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!/^[+()\-\s\d]{7,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.zip_code.trim()) e.zip_code = "Required";
    if (!form.home_size) e.home_size = "Select a size";
    if (!form.service_type) e.service_type = "Select a service";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
    } catch (err) {
      setErrors({ _form: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
      data-testid="quote-modal"
    >
      <div
        className="absolute inset-0 bg-[#0E1F44]/55 backdrop-blur-sm"
        onClick={closeQuote}
        data-testid="quote-modal-backdrop"
      />
      <div
        className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl px-7 py-8 sm:px-9 sm:py-10 max-h-[92vh] overflow-y-auto fade-up"
        style={{ animation: "fadeUp 0.35s ease both" }}
      >
        <button
          onClick={closeQuote}
          className="absolute top-5 right-5 text-[#1A2B4A] hover:text-[#173B86] transition"
          aria-label="Close"
          data-testid="quote-modal-close"
        >
          <X className="w-5 h-5" strokeWidth={2.2} />
        </button>

        {success ? (
          <div className="text-center py-6" data-testid="quote-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#DCEBFB] flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#173B86]" />
            </div>
            <h3 id="quote-modal-title" className="font-serif text-2xl text-[#173B86] mb-2">
              Thanks, Tina will contact you shortly.
            </h3>
            <p className="text-sm text-[#5b6a85] mb-6">
              We typically reply within 1 hour during business hours.
            </p>
            <button
              type="button"
              onClick={closeQuote}
              className="tc-btn-primary"
              data-testid="quote-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3
              id="quote-modal-title"
              className="font-serif text-3xl text-[#173B86] mb-2"
            >
              Book professional cleaning with a 20% discount
            </h3>
            <p className="text-sm text-[#5b6a85] mb-7">
              5-star Cleaning Service. Easy Scheduling. Happy Clients.
            </p>

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <Field
                label="Full Name"
                error={errors.full_name}
                testid="qm-full-name-field"
              >
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className={inputCls(errors.full_name)}
                  placeholder="Full Name"
                  aria-label="Full Name"
                  data-testid="qm-full-name"
                  required
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone Number" error={errors.phone} testid="qm-phone-field">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={inputCls(errors.phone)}
                    placeholder="Phone Number"
                    aria-label="Phone Number"
                    data-testid="qm-phone"
                    required
                  />
                </Field>
                <Field label="Email" error={errors.email} testid="qm-email-field">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={inputCls(errors.email)}
                    placeholder="Email"
                    aria-label="Email"
                    data-testid="qm-email"
                    required
                  />
                </Field>
              </div>

              <Field label="ZIP Code" error={errors.zip_code} testid="qm-zip-field">
                <input
                  type="text"
                  value={form.zip_code}
                  onChange={(e) => update("zip_code", e.target.value)}
                  className={inputCls(errors.zip_code)}
                  placeholder="ZIP Code"
                  aria-label="ZIP Code"
                  data-testid="qm-zip"
                  required
                />
              </Field>

              <Field label="Home Size" error={errors.home_size} testid="qm-home-size-field">
                <select
                  value={form.home_size}
                  onChange={(e) => update("home_size", e.target.value)}
                  className={selectCls(errors.home_size, !form.home_size)}
                  aria-label="Home Size"
                  data-testid="qm-home-size"
                  required
                >
                  <option value="">Select Size of Home</option>
                  {HOME_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Service Type"
                error={errors.service_type}
                testid="qm-service-field"
              >
                <select
                  value={form.service_type}
                  onChange={(e) => update("service_type", e.target.value)}
                  className={`${selectCls(errors.service_type, !form.service_type)} border-[#183B87]`}
                  aria-label="Service Type"
                  data-testid="qm-service"
                  required
                >
                  <option value="">Select Type of Service</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </Field>

              {errors._form && (
                <p className="text-red-600 text-sm" data-testid="qm-form-error">
                  {errors._form}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="tc-btn-primary w-full mt-3 flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="qm-submit"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending..." : "Get my Quote"}
              </button>

              <p className="text-center text-xs text-[#5b6a85] pt-1">
                By submitting, you agree to our{" "}
                <a
                  href="/privacy"
                  className="text-[#173B86] hover:underline"
                >
                  Privacy Policy
                </a>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, error, children, testid }) {
  return (
    <div data-testid={testid}>
      {children}
      {error && (
        <p className="text-red-600 text-xs mt-1 ml-4" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

const inputCls = (hasErr) =>
  `w-full rounded-full border ${
    hasErr ? "border-red-400" : "border-[#dde3ed]"
  } px-5 py-3.5 text-[14px] text-[#1A2B4A] placeholder:text-[#9aa3b6] focus:outline-none focus:border-[#183B87] focus:ring-2 focus:ring-[#DCEBFB] transition`;

const selectCls = (hasErr, isPlaceholder) =>
  `w-full rounded-full border ${
    hasErr ? "border-red-400" : "border-[#dde3ed]"
  } px-5 py-3.5 text-[14px] ${
    isPlaceholder ? "text-[#9aa3b6]" : "text-[#1A2B4A]"
  } bg-white appearance-none bg-no-repeat bg-[right_1.25rem_center] bg-[length:14px] focus:outline-none focus:border-[#183B87] focus:ring-2 focus:ring-[#DCEBFB] transition`;
